<template>
  <div v-if="src" class="iframe__wrapper" :style="iframeWrapperStyles">
    <iframe :src="iframeSrc" v-bind="iframeAttrs" :class="iframeDynamicClass" />
  </div>
</template>

<script>
import { removeEndingSlashes, parseUrl } from '@fmpedia/helpers'
import mixins from '@/utils/mixins'
import { COOKIE_ENTITY_TYPE } from 'enums/cmp'
import { mapActions } from 'vuex'
import { createCmpHandlerInstance } from 'enums/cmp/cmp-handler'
import { PROP_TYPES, propValidator } from '@/utils/validators'
import { DEFAULT_IFRAME_WIDTH, DEFAULT_IFRAME_HEIGHT } from 'enums/media-center'
import { urlFormatters } from '@/utils/mixins/general'
import { ELEMENT_WIDTH, ELEMENT_HEIGHT } from 'enums/data-attributes'

const COOKIE_ENTITY_BY_SOURCE = {}

export default {
  name: 'AIframe',
  mixins: [mixins.cmp, urlFormatters],
  inheritAttrs: false,
  props: {
    src: propValidator([PROP_TYPES.STRING], false),
    entityType: propValidator([PROP_TYPES.STRING], false),

    /**
     * Note that YouTube iframes will always be responsive even if this prop is
     * false
     */
    responsive: propValidator([PROP_TYPES.BOOLEAN], false, false),
    ignoreIframeSizes: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      iframeSrc: null
    }
  },
  computed: {
    srcAddress() {
      return removeEndingSlashes(parseUrl(this.src).address)
    },
    isConsentGiven() {
      return this.$_cmp_isConsentGivenForEntity(this.relevantCookieEntity)
    },
    relevantCookieEntity() {
      if (this.entityType) return this.entityType

      return (
        COOKIE_ENTITY_BY_SOURCE[this.srcAddress] || COOKIE_ENTITY_TYPE.UNKNOWN
      )
    },
    parsedWidthFromAttrs() {
      return +String(this.$attrs.width || '').replace(/px$/im, '')
    },
    parsedHeightFromAttrs() {
      return +String(this.$attrs.height || '').replace(/px$/im, '')
    },
    iframeWidth() {
      return this.isWidthAndHeightValid
        ? this.$attrs.width
        : DEFAULT_IFRAME_WIDTH
    },
    iframeHeight() {
      return this.isWidthAndHeightValid
        ? this.$attrs.height
        : DEFAULT_IFRAME_HEIGHT
    },
    isWidthAndHeightValid() {
      return this.parsedWidthFromAttrs > 0 && this.parsedHeightFromAttrs > 0
    },
    iframeAspectRatio() {
      return this.iframeWidth / this.iframeHeight
    },
    iframeAttrs() {
      return {
        ...this.$attrs,
        ...this.dimensionsAttrs
      }
    },
    isYoutubeIframe() {
      return this.isYoutubeUrl(this.src)
    },
    isLayoutResponsive() {
      return this.isYoutubeIframe || this.responsive
    },
    dimensionsAttrs() {
      if (this.ignoreIframeSizes) {
        return {
          width: '100%',
          height: '100%'
        }
      }

      const dataWidth = this.$attrs[ELEMENT_WIDTH]
      const dataHeight = this.$attrs[ELEMENT_HEIGHT]

      return {
        width: dataWidth || this.iframeWidth,
        height: dataHeight || this.iframeHeight
      }
    },
    iframeDynamicClass() {
      return {
        absolute: this.isLayoutResponsive,
        mocked: !this.isConsentGiven
      }
    },
    iframeWrapperStyles() {
      if (!this.isLayoutResponsive || this.ignoreIframeSizes) return {}

      return this.$helper.generateAspectRatioStyle(this.iframeAspectRatio)
    }
  },
  methods: {
    ...mapActions({
      registerHandlerThatRequiresConsent:
        'cmp/registerHandlerThatRequiresConsent'
    }),
    setIframeSource() {
      if (!this.src) return

      this.iframeSrc = this.src
    }
  },
  mounted() {
    this.registerHandlerThatRequiresConsent(
      createCmpHandlerInstance({
        handler: this.setIframeSource,
        entityType: this.relevantCookieEntity
      })
    )
  }
}
</script>

<style lang="scss" scoped>
.iframe__wrapper {
  width: 100%;
  position: relative;
  overflow: hidden;

  iframe {
    display: block;
    margin: 0 auto;
    max-width: 100%;

    &.absolute {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
    }

    &.mocked {
      background: $c--black;
    }
  }
}
</style>
